import {useDeepMemo} from '@wandb/weave/hookUtils';
import {type MutableRefObject, useCallback, useMemo, useRef} from 'react';

import {
  type RunSelectorGroupsLeanQuery,
  type RunSelectorGroupsLeanQueryVariables,
  type RunSelectorRunsQuery,
  type RunSelectorRunsQueryVariables,
  useRunSelectorGroupsLeanQuery,
  useRunSelectorRunsQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filters from '../../../util/filters';
import {usePinnedRunFilters} from '../../../util/hooks/usePinnedRunsFilters';
import {isNetworkRequestSettled} from '../../../util/isNetworkRequestSettled';
import * as Run from '../../../util/runs';
import {type RunSelectorRunsPartialVars} from '../types';

type DeltaVariables = Pick<
  RunSelectorRunsQueryVariables,
  'currentRuns' | 'lastUpdated'
>;

export type UseRunSelectorRunsVariablesArgs = {
  /** Determines how many runs will be returned on initial load and when loading more runs */
  limit: number;
  /** Variables that will be used to compute the full set of query variables */
  partialVariables: RunSelectorRunsPartialVars;
  /** If true, filters will be stricter so only selected (visualized) runs are returned */
  shouldOnlyShowSelected?: boolean;
  /** If true, delta variables are not sent since the lean grouped query has no delta support */
  useLeanGroupedRunsQuery?: boolean;
};

export type UseRunSelectorRunsQueryArgs = {
  variables?: RunSelectorRunsQueryVariables;
  /** If true, fetch grouped rows via the lean runSelectorGroups query instead of the full runs query */
  useLeanGroupedRunsQuery?: boolean;
  /** If true, will not query for runs */
  skip?: boolean;
  deltaVariablesRef: MutableRefObject<DeltaVariables>;
  onQueryCompleted: (
    data: RunSelectorRunsQuery,
    vars?: RunSelectorRunsQueryVariables
  ) => void;
  onLeanGroupQueryCompleted: (
    data: RunSelectorGroupsLeanQuery | undefined,
    vars?: RunSelectorRunsQueryVariables
  ) => void;
};

export const useVariables = ({
  limit,
  partialVariables,
  shouldOnlyShowSelected,
  useLeanGroupedRunsQuery = false,
}: UseRunSelectorRunsVariablesArgs) => {
  // Track these values so the backend knows what to compute deltas against.
  // Must use ref because we don't want to trigger a new request every time
  // these values are updated; instead, we just want to make sure they're
  // correctly populated when a request is made.
  const deltaVariablesRef = useRef<DeltaVariables>({
    currentRuns: [],
    lastUpdated: new Date(0).toISOString(),
  });

  const {
    projectInternalId,
    filters: baseFilters,
    selections,
    grouping,
    pinnedRunIds = [],
    baselineRunId,
    ...passThroughVars
  } = partialVariables;

  // passThroughVars needs to be memoized by value because it is recreated on every render
  // due to the spread operator
  const passThroughVarsMemoized = useDeepMemo(passThroughVars);

  // hack! baseFilters is changing on eyeball click due to a recent cache change.
  // this is a temporary fix to prevent the erroneous shimmer when baseFilters are
  // value-equal but erroneously not ref equal. TODO: remove once cache change
  // is fixed.
  const baseFiltersMemoized = useDeepMemo(baseFilters);

  const {excludedIDsFilter} = usePinnedRunFilters(pinnedRunIds, baselineRunId);

  const filters = useMemo(() => {
    const filters =
      shouldOnlyShowSelected && selections != null
        ? Filters.And([baseFiltersMemoized, selections])
        : baseFiltersMemoized;

    if (excludedIDsFilter) {
      return Filters.And([filters, excludedIDsFilter]);
    }
    return filters;
  }, [
    shouldOnlyShowSelected,
    selections,
    baseFiltersMemoized,
    excludedIDsFilter,
  ]);

  const variables = useMemo<RunSelectorRunsQueryVariables | undefined>(() => {
    if (!projectInternalId) {
      return undefined;
    }

    return {
      // project identifier
      projectInternalId,

      // delta vars
      currentRuns: useLeanGroupedRunsQuery
        ? []
        : deltaVariablesRef.current.currentRuns,
      // fresh lastUpdated time ensures correct results on query change
      // it should only be set to something else on polled requests
      lastUpdated: new Date(0).toISOString(),

      // logical vars
      ...passThroughVarsMemoized,
      filters: JSON.stringify(Filters.toMongo(filters)),
      groupKeys: grouping?.map(Run.keyToServerPath) ?? [],
      pinnedRunIds,
      limit,
    };
  }, [
    projectInternalId,
    grouping,
    passThroughVarsMemoized,
    filters,
    pinnedRunIds,
    limit,
    useLeanGroupedRunsQuery,
  ]);

  return {variables, deltaVariablesRef};
};

export const useRunSelectorRunsDataQuery = ({
  variables,
  useLeanGroupedRunsQuery = false,
  skip,
  deltaVariablesRef,
  onQueryCompleted,
  onLeanGroupQueryCompleted,
}: UseRunSelectorRunsQueryArgs) => {
  const leanQueryVariables = useMemo<
    RunSelectorGroupsLeanQueryVariables | undefined
  >(() => {
    if (!variables) {
      return undefined;
    }
    return {
      filters: variables.filters,
      groupKeys: variables.groupKeys,
      groupLevel: variables.groupLevel,
      limit: variables.limit,
      order: variables.order,
      projectInternalId: variables.projectInternalId,
    };
  }, [variables]);

  const queryResults = useRunSelectorRunsQuery({
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    skip: skip || !variables || useLeanGroupedRunsQuery,
    variables,
    onCompleted: data => onQueryCompleted(data, variables),
  });

  const leanQueryResults = useRunSelectorGroupsLeanQuery({
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    skip: skip || !leanQueryVariables || !useLeanGroupedRunsQuery,
    variables: leanQueryVariables,
    onCompleted: data => onLeanGroupQueryCompleted(data, variables),
  });

  const activeQueryResults = useLeanGroupedRunsQuery
    ? leanQueryResults
    : queryResults;

  const refetch = useCallback(async () => {
    if (useLeanGroupedRunsQuery) {
      const result = await leanQueryResults.refetch(leanQueryResults.variables);
      onLeanGroupQueryCompleted(result.data, variables);
      return result;
    }
    const refetchVariables = {
      ...queryResults.variables,
      ...deltaVariablesRef.current,
    };
    // Handle the refetched data via the returned promise rather than relying
    // on the query's `onCompleted` callback. Under Apollo Client v3, with
    // `fetchPolicy: 'no-cache'` and the default `notifyOnNetworkStatusChange:
    // false`, a `refetch()` does not surface the intermediate loading state, so
    // networkStatus goes ready -> ready and `onCompleted` is skipped (it only
    // fires on a networkStatus *change* into `ready`). That silently broke
    // live polling of the workspace runs table: new runs landed in
    // `queryResults.data` but never reached Redux (the UI's source of truth).
    const result = await queryResults.refetch(refetchVariables);
    // Pass the hook's `variables` (not the spread `refetchVariables`) so the
    // type matches what `onQueryCompleted` expects. `onQueryCompleted` only
    // reads `vars.pinnedRunIds`, which is identical in both, and this mirrors
    // the `onCompleted` path above.
    onQueryCompleted(result.data, variables);
    return result;
  }, [
    deltaVariablesRef,
    leanQueryResults,
    onLeanGroupQueryCompleted,
    onQueryCompleted,
    queryResults,
    useLeanGroupedRunsQuery,
    variables,
  ]);

  const isLoadingRuns =
    !skip && !isNetworkRequestSettled(activeQueryResults.networkStatus);

  return {
    data: activeQueryResults.data,
    error: activeQueryResults.error,
    isLoadingRuns,
    refetch,
  };
};
